import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { api, fetchCentersFromDB, isAdminUser } from "@/lib/api";
import { useActiveChannels, readChannelValue, channelDocKey, flattenChannels } from "@/hooks/useActiveChannels";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import {
  Loader2, RefreshCw, Copy, FileText, MessageSquare, Check, CalendarRange, CalendarDays, CalendarClock, Download, BarChart3,
} from "lucide-react";

// Inline Sales-vs-Expenses bar chart component reused across tabs
function SalesVsExpensesChart({ chartSeries, periodLabel }) {
  if (!chartSeries || chartSeries.length === 0) {
    return (
      <div className="text-center text-xs text-muted-foreground py-6 italic" data-testid="chart-empty">
        No chart data available
      </div>
    );
  }
  const fmt = (n) => `${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  return (
    <div className="w-full" data-testid="chart-container">
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartSeries} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={chartSeries.length > 8 ? -35 : 0}
                 textAnchor={chartSeries.length > 8 ? "end" : "middle"} height={50} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt} />
          <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="sales" name="Sales" fill="#800020" radius={[3, 3, 0, 0]} />
          <Bar dataKey="expenses" name="Expenses" fill="#C9A227" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      {periodLabel && (
        <p className="text-center text-xs text-muted-foreground mt-1">{periodLabel}</p>
      )}
    </div>
  );
}

const downloadBrandedPdf = async ({ session, center, periodType, payload, filenameHint, onErr, onOk }) => {
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${API}/api/daily-text/download-pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: session.token, center, period_type: periodType, ...payload }),
    });
    if (!res.ok) {
      let msg = "PDF download failed";
      try { const j = await res.json(); msg = j.detail || msg; } catch { /* ignore */ }
      onErr?.(msg);
      return;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filenameHint || `${center}_${periodType}`}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    onOk?.();
  } catch {
    onErr?.("PDF download failed");
  }
};

// =======================================
// DAILY TAB
// =======================================
function DailyTab({ session, centersList, isAdmin, isFranchiseOwner, canEdit }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(session?.center || "");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [textData, setTextData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [generatedText, setGeneratedText] = useState("");
  const [showChart, setShowChart] = useState(false);
  // Active delivery channels for the selected center (Swiggy+Zomato for
  // India, UberEats+DoorDash for PB-PERTH, Grubhub etc. for USA, all
  // resolved dynamically from Delivery Channel Master).
  const { channels: activeChannels } = useActiveChannels(session, selectedCenter);

  const generateText = async () => {
    if (!selectedCenter) { toast.error("Select a center"); return; }
    setLoading(true);
    setCopied(false);
    try {
      const overrides = Object.keys(editableData).length > 0 ? editableData : undefined;
      const res = await api.post("/daily-text/generate", {
        token: session.token, center: selectedCenter, date: selectedDate, overrides,
      });
      setTextData(res.data);
      const fresh = computeDerived(flattenChannels(res.data.data || {}, ""), new Set());
      setEditedKeys(new Set());
      setEditableData(fresh);
      setGeneratedText(res.data.text || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate text");
    } finally { setLoading(false); }
  };

  const refreshFromData = async () => {
    if (!selectedCenter) { toast.error("Select a center"); return; }
    setLoading(true); setCopied(false);
    try {
      const res = await api.post("/daily-text/generate", {
        token: session.token, center: selectedCenter, date: selectedDate,
      });
      setTextData(res.data);
      const fresh = computeDerived(flattenChannels(res.data.data || {}, ""), new Set());
      setEditedKeys(new Set());
      setEditableData(fresh);
      setGeneratedText(res.data.text || "");
      toast.success("Refreshed from stored data");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to refresh");
    } finally { setLoading(false); }
  };

  const regenerateFromEdits = () => {
    const d = editableData;
    const fmt = (v) => `${Math.round(Number(v) || 0)}/-`;
    const dt = selectedDate.split("-");
    const displayDate = dt.length === 3 ? `${dt[2]}/${dt[1]}/${dt[0]}` : selectedDate;
    // Delivery channels are dynamic — loop the master. Numbering starts
    // at 7 (Card/UPI took 5-6) and continues through Cash Sale after.
    const channelLines = (activeChannels || []).map((ch, i) => {
      const val = readChannelValue(d, ch, "");
      return `${7 + i}. ${ch.label.toUpperCase()} = ${fmt(val)}`;
    });
    const afterChannels = 7 + (activeChannels || []).length;
    const lines = [
      "Jai Hind Namskar 🙏", "",
      `Date ${displayDate}`, "",
      `1. Opening Bal = ${fmt(d.opening_balance)}`,
      `2. Deposit = ${fmt(d.deposit)}`,
      `3. Withdrawl = ${fmt(d.withdrawal)}`,
      `4. Total Sale = ${fmt(d.total_sale)}`,
      `5. Card = ${fmt(d.card)}`,
      `6. Phone Pay = ${fmt(d.phone_pay)}`,
      ...channelLines,
      `${afterChannels}. Due Amount = ${fmt(d.due_amount)}`,
      `${afterChannels + 1}. Cash sale = ${fmt(d.cash_sale)}`,
      `${afterChannels + 2}. Online Expense = ${fmt(d.online_expense)}`,
      `${afterChannels + 3}. Cash Expense = ${fmt(d.cash_expense)}`,
      `${afterChannels + 4}. Cash In Hand = ${fmt(d.cash_in_hand)}`,
      `${afterChannels + 5}. Bal. Petty cash = ${fmt(d.petty_cash_balance)}`,
      `${afterChannels + 6}. Total No. of guest = ${Math.round(Number(d.total_guests) || 0)}`,
      `${afterChannels + 7}. APC = ${Math.round(Number(d.apc) || 0)}`,
      `${afterChannels + 8}. No. Of Drinks = ${Math.round(Number(d.num_drinks) || 0)}`,
      `${afterChannels + 9}. No of sides sold = ${Math.round(Number(d.num_sides) || 0)}`,
    ];
    // "Cancelled <Platform> Order" counters are specific to Swiggy / Zomato
    // (India-only integrations at time of writing). Only surface them when
    // the active channel master for this center actually includes them.
    const activeKeys = new Set((activeChannels || []).map((c) => c.key));
    if (activeKeys.has("zomato")) {
      lines.push(`    Cancelled Zomato Order = ${Math.round(Number(d.cancelled_zomato) || 0)}`);
    }
    if (activeKeys.has("swiggy")) {
      lines.push(`${afterChannels + 10}. Cancelled Swiggy Order = ${Math.round(Number(d.cancelled_swiggy) || 0)}`);
    }
    setGeneratedText(lines.join("\n"));
  };

  // Recompute derived fields based on user edits.
  // Derived fields:
  //   cash_sale     = total_sale − card − phone_pay − swiggy − zomato − due_amount
  //   apc           = total_sale / total_guests
  //   cash_in_hand  = opening_balance + cash_sale − cash_expense − deposit − withdrawal
  // Each one is auto-updated unless the user has explicitly typed a value
  // (we track those via editedKeys).
  const num = (v) => Number(v) || 0;
  const computeDerived = (data, editedKeys = new Set()) => {
    const next = { ...data };
    const ts = num(next.total_sale);
    const card = num(next.card);
    const pp = num(next.phone_pay);
    // Delivery channels come from Delivery Channel Master. Sum them
    // so the cash-sale derivation works for any country / center.
    const deliveryTotal = (activeChannels || []).reduce(
      (sum, ch) => sum + num(next[ch.key]), 0);
    const due = num(next.due_amount);
    const op = num(next.opening_balance);
    const dep = num(next.deposit);
    const wd = num(next.withdrawal);
    const cexp = num(next.cash_expense);
    const tg = num(next.total_guests);

    if (!editedKeys.has("cash_sale")) {
      next.cash_sale = Math.max(0, Math.round(ts - card - pp - deliveryTotal - due));
    }
    if (!editedKeys.has("apc")) {
      next.apc = tg > 0 ? Math.round(ts / tg) : 0;
    }
    if (!editedKeys.has("cash_in_hand")) {
      next.cash_in_hand = Math.round(op + num(next.cash_sale) - cexp - dep - wd);
    }
    return next;
  };

  // Track which keys the user has explicitly overridden
  const [editedKeys, setEditedKeys] = useState(new Set());

  const updateField = (key, value) => {
    setEditedKeys(prev => {
      // If user clears the field, treat as auto again
      const next = new Set(prev);
      if (value === "" || value === null) next.delete(key);
      else next.add(key);
      return next;
    });
    setEditableData(p => computeDerived({ ...p, [key]: value }, new Set([...editedKeys, key])));
  };

  // Recompute derived whenever raw data is loaded / refreshed
  useEffect(() => {
    if (Object.keys(editableData).length === 0) return;
    regenerateFromEdits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData]);

  const copyText = () => {
    navigator.clipboard.writeText(generatedText).then(() => {
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Editable fields — delivery-channel entries are dynamic from the master.
  const channelFields = (activeChannels || []).map((ch) => ({
    key: channelDocKey(ch, ""),      // "swiggy" | "zomato" | "delivery_extra.grubhub" | ...
    label: ch.label,
    isChannel: true,
    channel: ch,
  }));
  const fields = [
    { key: "opening_balance", label: "Opening Bal" },
    { key: "deposit", label: "Deposit" },
    { key: "withdrawal", label: "Withdrawal" },
    { key: "total_sale", label: "Total Sale" },
    { key: "card", label: "Card" },
    { key: "phone_pay", label: "Phone Pay / UPI" },
    ...channelFields,
    { key: "due_amount", label: "Due Amount" },
    { key: "cash_sale", label: "Cash Sale" },
    { key: "online_expense", label: "Online Expense" },
    { key: "cash_expense", label: "Cash Expense" },
    { key: "cash_in_hand", label: "Cash In Hand" },
    { key: "petty_cash_balance", label: "Petty Cash Bal" },
    { key: "total_guests", label: "Total Guests" },
    { key: "apc", label: "APC" },
    { key: "num_drinks", label: "No. of Drinks" },
    { key: "num_sides", label: "No. of Sides Sold" },
  ];
  const activeChannelKeys = new Set((activeChannels || []).map((c) => c.key));
  const gatedFields = fields.filter((f) => {
    // "Cancelled Zomato/Swiggy" counters are only relevant for centers whose
    // active channels actually include those platforms.
    if (f.key === "cancelled_zomato" && !activeChannelKeys.has("zomato")) return false;
    if (f.key === "cancelled_swiggy" && !activeChannelKeys.has("swiggy")) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Center</Label>
              <select value={selectedCenter} onChange={e => setSelectedCenter(e.target.value)}
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                data-testid="text-center-select" disabled={!isAdmin && !isFranchiseOwner}>
                {!isAdmin && <option value={session?.center}>{session?.center}</option>}
                {isAdmin && centersList.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                className="w-[160px]" data-testid="text-date" />
            </div>
            <Button onClick={generateText} disabled={loading} data-testid="generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Generate Text
            </Button>
            <Button onClick={refreshFromData} variant="outline" disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh from Data
            </Button>
            {generatedText && (
              <Button onClick={copyText} variant={copied ? "default" : "outline"}
                className={copied ? "bg-green-600 hover:bg-green-700" : "border-green-500 text-green-600"}
                data-testid="copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Copy Text"}
              </Button>
            )}
            {textData && (
              <Button variant="outline"
                className="border-rose-500 text-rose-600 hover:bg-rose-50"
                onClick={() => downloadBrandedPdf({
                  session, center: selectedCenter, periodType: "daily",
                  payload: { date: selectedDate, include_chart: showChart },
                  filenameHint: `${selectedCenter}_daily_${selectedDate}`,
                  onErr: (m) => toast.error(m), onOk: () => toast.success("PDF downloaded"),
                })}
                data-testid="daily-pdf-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            )}
            {textData && (
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-2 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100"
                data-testid="daily-show-chart-toggle">
                <input type="checkbox" checked={showChart}
                  onChange={(e) => setShowChart(e.target.checked)}
                  className="cursor-pointer" />
                <BarChart3 className="w-4 h-4 text-amber-700" />
                <span className="text-amber-800 font-medium">Show Chart</span>
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {textData && showChart && (
        <Card className="border-2 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
              <BarChart3 className="w-4 h-4" /> Sales vs Expenses (Daily)
            </CardTitle>
            <CardDescription className="text-xs">
              Sales: <strong className="text-[#800020]">Rs. {(editableData.total_sale || 0).toLocaleString("en-IN")}</strong>
              {" · "}Expenses: <strong className="text-[#C9A227]">Rs. {((editableData.online_expense || 0) + (editableData.cash_expense || 0)).toLocaleString("en-IN")}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SalesVsExpensesChart
              chartSeries={[{
                label: selectedDate,
                date: selectedDate,
                sales: Number(editableData.total_sale) || 0,
                expenses: (Number(editableData.online_expense) || 0) + (Number(editableData.cash_expense) || 0),
              }]}
              periodLabel={`Date: ${selectedDate}`} />
          </CardContent>
        </Card>
      )}

      {textData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {canEdit && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Edit Values</CardTitle>
                <CardDescription className="text-xs">
                  {textData.has_sales_data ? (
                    <Badge className="bg-green-100 text-green-800">Auto-filled from sales data</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">No sales data — enter manually</Badge>
                  )}
                  {textData.expense_count > 0 && (
                    <Badge className="bg-blue-100 text-blue-800 ml-2">{textData.expense_count} expenses found</Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {gatedFields.map(f => {
                    const isDerived = ["cash_sale", "apc", "cash_in_hand"].includes(f.key);
                    const isAuto = isDerived && !editedKeys.has(f.key);
                    return (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className="text-[11px] w-28 text-right text-muted-foreground shrink-0">{f.label}</Label>
                        <div className="relative flex-1">
                          <Input type="number" value={editableData[f.key] ?? ""} className={`h-8 text-xs ${isAuto ? "bg-emerald-50 border-emerald-200" : ""}`}
                            onChange={e => updateField(f.key, parseFloat(e.target.value) || 0)}
                            data-testid={`field-${f.key}`} />
                          {isAuto && (
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-emerald-600 font-semibold pointer-events-none" title="Auto-calculated from above fields">AUTO</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-3">
                  Fields tagged <span className="text-emerald-600 font-semibold">AUTO</span> recalculate live from your edits (Cash Sale = Sale − Card − UPI − Swiggy − Zomato − Due · APC = Sale ÷ Guests · Cash In Hand = Opening + Cash Sale − Cash Exp − Deposit − Withdrawal). Type in any AUTO field to override; clear it to switch back to auto.
                </p>
              </CardContent>
            </Card>
          )}
          <Card className="border-2 border-green-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                <MessageSquare className="w-4 h-4" /> WhatsApp Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm bg-green-50 p-4 rounded-lg font-mono leading-relaxed border"
                data-testid="text-preview">{generatedText}</pre>
              <Button onClick={copyText} className="mt-3 w-full" variant={copied ? "default" : "outline"}
                data-testid="copy-btn-bottom">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied to Clipboard!" : "Copy to Clipboard"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// =======================================
// WEEKLY TAB
// =======================================
function WeeklyTab({ session, centersList, isAdmin, isFranchiseOwner, canEdit }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(session?.center || "");
  // Default to current Monday
  const today = new Date();
  const day = today.getDay() || 7; // Sun=0 -> 7
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day - 1));
  const [weekDate, setWeekDate] = useState(monday.toISOString().split("T")[0]);

  const [textData, setTextData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [editableCategories, setEditableCategories] = useState([]);
  const [generatedText, setGeneratedText] = useState("");
  const [showChart, setShowChart] = useState(false);
  const { channels: activeChannels } = useActiveChannels(session, selectedCenter);

  const generate = async () => {
    if (!selectedCenter) { toast.error("Select a center"); return; }
    setLoading(true); setCopied(false);
    try {
      const res = await api.post("/daily-text/generate-weekly", {
        token: session.token, center: selectedCenter, week_date: weekDate,
      });
      setTextData(res.data);
      setEditableData(flattenChannels(res.data.data || {}, "total_"));
      setEditableCategories(res.data.data?.expense_categories || []);
      setGeneratedText(res.data.text || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate weekly text");
    } finally { setLoading(false); }
  };

  const refresh = async () => {
    if (!selectedCenter) { toast.error("Select a center"); return; }
    setLoading(true); setCopied(false);
    try {
      const res = await api.post("/daily-text/generate-weekly", {
        token: session.token, center: selectedCenter, week_date: weekDate,
      });
      setTextData(res.data);
      setEditableData(flattenChannels(res.data.data || {}, "total_"));
      setEditableCategories(res.data.data?.expense_categories || []);
      setGeneratedText(res.data.text || "");
      toast.success("Refreshed from stored data");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to refresh");
    } finally { setLoading(false); }
  };

  // Regenerate locally from edits (mirror of backend formatter)
  const regenerate = () => {
    const d = { ...editableData, expense_categories: editableCategories };
    const fmt = (v) => `${Math.round(Number(v) || 0)}/-`;
    const ord = (n) => {
      if (n >= 11 && n <= 13) return `${n}th`;
      const last = n % 10;
      return `${n}${last === 1 ? "st" : last === 2 ? "nd" : last === 3 ? "rd" : "th"}`;
    };
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    let rangeStr = `${d.week_start} to ${d.week_end}`;
    try {
      const ws = new Date(d.week_start + "T00:00:00");
      const we = new Date(d.week_end + "T00:00:00");
      rangeStr = `${ord(ws.getDate())} ${monthNames[ws.getMonth()]} ${ws.getFullYear()} to ${ord(we.getDate())} ${monthNames[we.getMonth()]} ${we.getFullYear()}`;
    } catch { /* keep fallback */ }

    const lines = [
      "Jai Hind Namskar 🙏", "",
      rangeStr, "",
      `↪️ Total Sale = ${fmt(d.total_sale)}`,
      `↪️ Total Card = ${fmt(d.total_card)}`,
      `↪️ Total Deposit = ${fmt(d.total_deposit)}`,
      `↪️ Total Withdrawl = ${fmt(d.total_withdrawal)}`,
      "",
      "DESCRIPTION Expenses",
    ];
    const letters = "abcdefghij";
    if ((d.expense_categories || []).length > 0) {
      d.expense_categories.forEach((c, i) => {
        const letter = i < letters.length ? letters[i] : `${i + 1}`;
        lines.push(`${letter}) ${c.name} = ${fmt(c.amount)}`);
      });
    } else {
      lines.push("(No expenses recorded)");
    }
    lines.push("");
    // Delivery channel totals — dynamic from Delivery Channel Master.
    (activeChannels || []).forEach((ch) => {
      const v = readChannelValue(d, ch, "total_");
      lines.push(`↪️ Total ${ch.label} = ${fmt(v)}`);
    });
    lines.push(
      `↪️ Total Paytm = ${fmt(d.total_paytm)}`,
      `↪️ Total Bharat Pay = ${fmt(d.total_bharat_pay)}`,
      `↪️ Total Cash Sale = ${fmt(d.total_cash_sale)}`,
      `↪️ Total Cash Expenses = ${fmt(d.total_cash_expenses)}`,
      `↪️ Total Cash In Hand = ${fmt(d.total_cash_in_hand)}`,
      `↪️ Total Online Expenses = ${fmt(d.total_online_expenses)}`,
      `↪️ APC = ${Math.round(Number(d.apc) || 0)}`,
      `↪️ Total No. Of Guest = ${Math.round(Number(d.total_guests) || 0)}`,
    );
    setGeneratedText(lines.join("\n"));
  };

  useEffect(() => {
    if (Object.keys(editableData).length > 0) regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData, editableCategories]);

  const copyText = () => {
    navigator.clipboard.writeText(generatedText).then(() => {
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const updateField = (key, value) => setEditableData(p => ({ ...p, [key]: value }));
  const updateCategory = (idx, key, value) => {
    setEditableCategories(prev => prev.map((c, i) => i === idx ? { ...c, [key]: key === "amount" ? (parseFloat(value) || 0) : value } : c));
  };
  const addCategory = () => setEditableCategories(p => [...p, { name: "New", amount: 0 }]);
  const removeCategory = (idx) => setEditableCategories(p => p.filter((_, i) => i !== idx));

  const editFields = [
    { key: "total_sale", label: "Total Sale" },
    { key: "total_card", label: "Total Card" },
    { key: "total_deposit", label: "Total Deposit" },
    { key: "total_withdrawal", label: "Total Withdrawl" },
    // Delivery channel totals — dynamic from Delivery Channel Master.
    ...((activeChannels || []).map((ch) => ({ key: `total_${ch.key}`, label: `Total ${ch.label}` }))),
    { key: "total_paytm", label: "Total Paytm" },
    { key: "total_bharat_pay", label: "Total Bharat Pay" },
    { key: "total_cash_sale", label: "Total Cash Sale" },
    { key: "total_cash_expenses", label: "Total Cash Expenses" },
    { key: "total_online_expenses", label: "Total Online Expenses" },
    { key: "total_cash_in_hand", label: "Total Cash In Hand" },
    { key: "total_guests", label: "Total No. Of Guest" },
    { key: "apc", label: "APC" },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Center</Label>
              <select value={selectedCenter} onChange={e => setSelectedCenter(e.target.value)}
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                data-testid="weekly-center-select" disabled={!isAdmin && !isFranchiseOwner}>
                {!isAdmin && <option value={session?.center}>{session?.center}</option>}
                {isAdmin && centersList.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Pick any date in the week (Mon-Sun snap)</Label>
              <Input type="date" value={weekDate} onChange={e => setWeekDate(e.target.value)}
                className="w-[180px]" data-testid="weekly-date" />
            </div>
            <Button onClick={generate} disabled={loading} data-testid="weekly-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarRange className="w-4 h-4 mr-2" />}
              Generate Weekly Text
            </Button>
            <Button onClick={refresh} variant="outline" disabled={loading} data-testid="weekly-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh from Data
            </Button>
            {generatedText && (
              <Button onClick={copyText} variant={copied ? "default" : "outline"}
                className={copied ? "bg-green-600 hover:bg-green-700" : "border-green-500 text-green-600"}
                data-testid="weekly-copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Copy Text"}
              </Button>
            )}
            {textData && (
              <Button variant="outline"
                className="border-rose-500 text-rose-600 hover:bg-rose-50"
                onClick={() => downloadBrandedPdf({
                  session, center: selectedCenter, periodType: "weekly",
                  payload: { week_date: weekDate, include_chart: showChart },
                  filenameHint: `${selectedCenter}_weekly_${textData?.week_start || weekDate}`,
                  onErr: (m) => toast.error(m), onOk: () => toast.success("PDF downloaded"),
                })}
                data-testid="weekly-pdf-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            )}
            {textData && (
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-2 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100"
                data-testid="weekly-show-chart-toggle">
                <input type="checkbox" checked={showChart}
                  onChange={(e) => setShowChart(e.target.checked)}
                  className="cursor-pointer" />
                <BarChart3 className="w-4 h-4 text-amber-700" />
                <span className="text-amber-800 font-medium">Show Chart</span>
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {textData && showChart && (
        <Card className="border-2 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
              <BarChart3 className="w-4 h-4" /> Sales vs Expenses (Weekly)
            </CardTitle>
            <CardDescription className="text-xs">
              Total Sales: <strong className="text-[#800020]">Rs. {(textData.data?.total_sale || 0).toLocaleString("en-IN")}</strong>
              {" · "}Total Expenses: <strong className="text-[#C9A227]">Rs. {(textData.data?.total_expenses || 0).toLocaleString("en-IN")}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SalesVsExpensesChart chartSeries={textData.data?.chart_series || []}
              periodLabel={`${textData.week_start} → ${textData.week_end}`} />
          </CardContent>
        </Card>
      )}

      {textData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {canEdit && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Edit Values</CardTitle>
                <CardDescription className="text-xs">
                  {textData.has_sales_data ? (
                    <Badge className="bg-green-100 text-green-800">Aggregated from {textData.sales_days} day(s)</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">No sales data this week</Badge>
                  )}
                  {textData.expense_count > 0 && (
                    <Badge className="bg-blue-100 text-blue-800 ml-2">{textData.expense_count} expenses</Badge>
                  )}
                  {textData.is_international && (
                    <Badge className="bg-purple-100 text-purple-800 ml-2">International (DoorDash shown)</Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {editFields
                    .filter(() => true)
                    .map(f => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className="text-[11px] w-32 text-right text-muted-foreground shrink-0">{f.label}</Label>
                        <Input type="number" value={editableData[f.key] ?? ""} className="h-8 text-xs"
                          onChange={e => updateField(f.key, parseFloat(e.target.value) || 0)}
                          data-testid={`weekly-field-${f.key}`} />
                      </div>
                    ))}
                </div>
                {/* Editable expense categories */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium">Expense Categories (Description Expenses)</Label>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addCategory} data-testid="weekly-add-cat-btn">
                      + Add
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {editableCategories.length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No expense categories</p>
                    )}
                    {editableCategories.map((c, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`weekly-cat-row-${idx}`}>
                        <span className="text-xs text-muted-foreground w-4">{String.fromCharCode(97 + idx)})</span>
                        <Input value={c.name} className="h-8 text-xs flex-1"
                          onChange={e => updateCategory(idx, "name", e.target.value)} />
                        <Input type="number" value={c.amount} className="h-8 text-xs w-28"
                          onChange={e => updateCategory(idx, "amount", e.target.value)} />
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500"
                          onClick={() => removeCategory(idx)} data-testid={`weekly-cat-remove-${idx}`}>×</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-2 border-green-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                <MessageSquare className="w-4 h-4" /> WhatsApp Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm bg-green-50 p-4 rounded-lg font-mono leading-relaxed border"
                data-testid="weekly-text-preview">{generatedText}</pre>
              <Button onClick={copyText} className="mt-3 w-full" variant={copied ? "default" : "outline"}
                data-testid="weekly-copy-btn-bottom">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied to Clipboard!" : "Copy to Clipboard"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// =======================================
// MONTHLY TAB
// =======================================
function MonthlyTab({ session, centersList, isAdmin, isFranchiseOwner, canEdit }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(session?.center || "");
  // Default to current month
  const today = new Date();
  const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [monthValue, setMonthValue] = useState(ym);

  const [textData, setTextData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [editableCategories, setEditableCategories] = useState([]);
  const [generatedText, setGeneratedText] = useState("");
  const [showChart, setShowChart] = useState(false);
  const { channels: activeChannels } = useActiveChannels(session, selectedCenter);

  const generate = async () => {
    if (!selectedCenter) { toast.error("Select a center"); return; }
    if (!monthValue) { toast.error("Pick a month"); return; }
    setLoading(true); setCopied(false);
    try {
      const res = await api.post("/daily-text/generate-monthly", {
        token: session.token, center: selectedCenter, month: monthValue,
      });
      setTextData(res.data);
      setEditableData(flattenChannels(res.data.data || {}, "total_"));
      setEditableCategories(res.data.data?.expense_categories || []);
      setGeneratedText(res.data.text || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate monthly text");
    } finally { setLoading(false); }
  };

  const refresh = async () => {
    if (!selectedCenter) { toast.error("Select a center"); return; }
    setLoading(true); setCopied(false);
    try {
      const res = await api.post("/daily-text/generate-monthly", {
        token: session.token, center: selectedCenter, month: monthValue,
      });
      setTextData(res.data);
      setEditableData(flattenChannels(res.data.data || {}, "total_"));
      setEditableCategories(res.data.data?.expense_categories || []);
      setGeneratedText(res.data.text || "");
      toast.success("Refreshed from stored data");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to refresh");
    } finally { setLoading(false); }
  };

  const regenerate = () => {
    const d = { ...editableData, expense_categories: editableCategories };
    const fmt = (v) => `${Math.round(Number(v) || 0)}/-`;
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    let rangeStr = `${d.month || monthValue} (Monthly Summary)`;
    try {
      if (d.month_start) {
        const ms = new Date(d.month_start + "T00:00:00");
        rangeStr = `${monthNames[ms.getMonth()]} ${ms.getFullYear()} (Monthly Summary)`;
      }
    } catch { /* keep fallback */ }

    const lines = [
      "Jai Hind Namskar 🙏", "",
      rangeStr, "",
      `↪️ Total Sale = ${fmt(d.total_sale)}`,
      `↪️ Total Card = ${fmt(d.total_card)}`,
      `↪️ Total Deposit = ${fmt(d.total_deposit)}`,
      `↪️ Total Withdrawl = ${fmt(d.total_withdrawal)}`,
      "",
      "DESCRIPTION Expenses",
    ];
    const letters = "abcdefghij";
    if ((d.expense_categories || []).length > 0) {
      d.expense_categories.forEach((c, i) => {
        const letter = i < letters.length ? letters[i] : `${i + 1}`;
        lines.push(`${letter}) ${c.name} = ${fmt(c.amount)}`);
      });
    } else {
      lines.push("(No expenses recorded)");
    }
    lines.push("");
    // Delivery channel totals — dynamic from Delivery Channel Master.
    (activeChannels || []).forEach((ch) => {
      const v = readChannelValue(d, ch, "total_");
      lines.push(`↪️ Total ${ch.label} = ${fmt(v)}`);
    });
    lines.push(
      `↪️ Total Paytm = ${fmt(d.total_paytm)}`,
      `↪️ Total Bharat Pay = ${fmt(d.total_bharat_pay)}`,
      `↪️ Total Cash Sale = ${fmt(d.total_cash_sale)}`,
      `↪️ Total Cash Expenses = ${fmt(d.total_cash_expenses)}`,
      `↪️ Total Cash In Hand = ${fmt(d.total_cash_in_hand)}`,
      `↪️ Total Online Expenses = ${fmt(d.total_online_expenses)}`,
      `↪️ APC = ${Math.round(Number(d.apc) || 0)}`,
      `↪️ Total No. Of Guest = ${Math.round(Number(d.total_guests) || 0)}`,
    );
    setGeneratedText(lines.join("\n"));
  };

  useEffect(() => {
    if (Object.keys(editableData).length > 0) regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData, editableCategories]);

  const copyText = () => {
    navigator.clipboard.writeText(generatedText).then(() => {
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const updateField = (key, value) => setEditableData(p => ({ ...p, [key]: value }));
  const updateCategory = (idx, key, value) => {
    setEditableCategories(prev => prev.map((c, i) => i === idx ? { ...c, [key]: key === "amount" ? (parseFloat(value) || 0) : value } : c));
  };
  const addCategory = () => setEditableCategories(p => [...p, { name: "New", amount: 0 }]);
  const removeCategory = (idx) => setEditableCategories(p => p.filter((_, i) => i !== idx));

  const editFields = [
    { key: "total_sale", label: "Total Sale" },
    { key: "total_card", label: "Total Card" },
    { key: "total_deposit", label: "Total Deposit" },
    { key: "total_withdrawal", label: "Total Withdrawl" },
    // Delivery channel totals — dynamic from Delivery Channel Master.
    ...((activeChannels || []).map((ch) => ({ key: `total_${ch.key}`, label: `Total ${ch.label}` }))),
    { key: "total_paytm", label: "Total Paytm" },
    { key: "total_bharat_pay", label: "Total Bharat Pay" },
    { key: "total_cash_sale", label: "Total Cash Sale" },
    { key: "total_cash_expenses", label: "Total Cash Expenses" },
    { key: "total_online_expenses", label: "Total Online Expenses" },
    { key: "total_cash_in_hand", label: "Total Cash In Hand" },
    { key: "total_guests", label: "Total No. Of Guest" },
    { key: "apc", label: "APC" },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Center</Label>
              <select value={selectedCenter} onChange={e => setSelectedCenter(e.target.value)}
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                data-testid="monthly-center-select" disabled={!isAdmin && !isFranchiseOwner}>
                {!isAdmin && <option value={session?.center}>{session?.center}</option>}
                {isAdmin && centersList.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Month</Label>
              <Input type="month" value={monthValue} onChange={e => setMonthValue(e.target.value)}
                className="w-[180px]" data-testid="monthly-date" />
            </div>
            <Button onClick={generate} disabled={loading} data-testid="monthly-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-2" />}
              Generate Monthly Text
            </Button>
            <Button onClick={refresh} variant="outline" disabled={loading} data-testid="monthly-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh from Data
            </Button>
            {generatedText && (
              <Button onClick={copyText} variant={copied ? "default" : "outline"}
                className={copied ? "bg-green-600 hover:bg-green-700" : "border-green-500 text-green-600"}
                data-testid="monthly-copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Copy Text"}
              </Button>
            )}
            {textData && (
              <Button variant="outline"
                className="border-rose-500 text-rose-600 hover:bg-rose-50"
                onClick={() => downloadBrandedPdf({
                  session, center: selectedCenter, periodType: "monthly",
                  payload: { month: monthValue, include_chart: showChart },
                  filenameHint: `${selectedCenter}_monthly_${monthValue}`,
                  onErr: (m) => toast.error(m), onOk: () => toast.success("PDF downloaded"),
                })}
                data-testid="monthly-pdf-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            )}
            {textData && (
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-2 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100"
                data-testid="monthly-show-chart-toggle">
                <input type="checkbox" checked={showChart}
                  onChange={(e) => setShowChart(e.target.checked)}
                  className="cursor-pointer" />
                <BarChart3 className="w-4 h-4 text-amber-700" />
                <span className="text-amber-800 font-medium">Show Chart</span>
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {textData && showChart && (
        <Card className="border-2 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
              <BarChart3 className="w-4 h-4" /> Sales vs Expenses (Monthly)
            </CardTitle>
            <CardDescription className="text-xs">
              Total Sales: <strong className="text-[#800020]">Rs. {(textData.data?.total_sale || 0).toLocaleString("en-IN")}</strong>
              {" · "}Total Expenses: <strong className="text-[#C9A227]">Rs. {(textData.data?.total_expenses || 0).toLocaleString("en-IN")}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SalesVsExpensesChart chartSeries={textData.data?.chart_series || []}
              periodLabel={`${textData.month_start} → ${textData.month_end}`} />
          </CardContent>
        </Card>
      )}

      {textData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {canEdit && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Edit Values</CardTitle>
                <CardDescription className="text-xs">
                  {textData.has_sales_data ? (
                    <Badge className="bg-green-100 text-green-800">Aggregated from {textData.sales_days} day(s)</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">No sales data this month</Badge>
                  )}
                  {textData.expense_count > 0 && (
                    <Badge className="bg-blue-100 text-blue-800 ml-2">{textData.expense_count} expenses</Badge>
                  )}
                  {textData.is_international && (
                    <Badge className="bg-purple-100 text-purple-800 ml-2">International (DoorDash shown)</Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {editFields
                    .filter(() => true)
                    .map(f => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className="text-[11px] w-32 text-right text-muted-foreground shrink-0">{f.label}</Label>
                        <Input type="number" value={editableData[f.key] ?? ""} className="h-8 text-xs"
                          onChange={e => updateField(f.key, parseFloat(e.target.value) || 0)}
                          data-testid={`monthly-field-${f.key}`} />
                      </div>
                    ))}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium">Expense Categories (Description Expenses)</Label>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addCategory} data-testid="monthly-add-cat-btn">
                      + Add
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {editableCategories.length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No expense categories</p>
                    )}
                    {editableCategories.map((c, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`monthly-cat-row-${idx}`}>
                        <span className="text-xs text-muted-foreground w-4">{String.fromCharCode(97 + idx)})</span>
                        <Input value={c.name} className="h-8 text-xs flex-1"
                          onChange={e => updateCategory(idx, "name", e.target.value)} />
                        <Input type="number" value={c.amount} className="h-8 text-xs w-28"
                          onChange={e => updateCategory(idx, "amount", e.target.value)} />
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500"
                          onClick={() => removeCategory(idx)} data-testid={`monthly-cat-remove-${idx}`}>×</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-2 border-green-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                <MessageSquare className="w-4 h-4" /> WhatsApp Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm bg-green-50 p-4 rounded-lg font-mono leading-relaxed border"
                data-testid="monthly-text-preview">{generatedText}</pre>
              <Button onClick={copyText} className="mt-3 w-full" variant={copied ? "default" : "outline"}
                data-testid="monthly-copy-btn-bottom">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied to Clipboard!" : "Copy to Clipboard"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// =======================================
// YEARLY TAB
// =======================================
function YearlyTab({ session, centersList, isAdmin, isFranchiseOwner, canEdit }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(session?.center || "");
  const [year, setYear] = useState(new Date().getFullYear());

  const [textData, setTextData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [editableCategories, setEditableCategories] = useState([]);
  const [generatedText, setGeneratedText] = useState("");
  const [showChart, setShowChart] = useState(false);
  const { channels: activeChannels } = useActiveChannels(session, selectedCenter);

  const generate = async () => {
    if (!selectedCenter) { toast.error("Select a center"); return; }
    if (!year || isNaN(year)) { toast.error("Pick a valid year"); return; }
    setLoading(true); setCopied(false);
    try {
      const res = await api.post("/daily-text/generate-yearly", {
        token: session.token, center: selectedCenter, year: parseInt(year, 10),
      });
      setTextData(res.data);
      setEditableData(flattenChannels(res.data.data || {}, "total_"));
      setEditableCategories(res.data.data?.expense_categories || []);
      setGeneratedText(res.data.text || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate yearly text");
    } finally { setLoading(false); }
  };

  const refresh = generate; // same call

  const regenerate = () => {
    const d = { ...editableData, expense_categories: editableCategories };
    const fmt = (v) => `${Math.round(Number(v) || 0)}/-`;
    const rangeStr = d.year_label || `${d.year_start || ""} to ${d.year_end || ""}`;
    const lines = [
      "Jai Hind Namskar 🙏", "",
      rangeStr, "",
      `↪️ Total Sale = ${fmt(d.total_sale)}`,
      `↪️ Total Card = ${fmt(d.total_card)}`,
      `↪️ Total Deposit = ${fmt(d.total_deposit)}`,
      `↪️ Total Withdrawl = ${fmt(d.total_withdrawal)}`,
      "",
      "DESCRIPTION Expenses",
    ];
    const letters = "abcdefghij";
    if ((d.expense_categories || []).length > 0) {
      d.expense_categories.forEach((c, i) => {
        const letter = i < letters.length ? letters[i] : `${i + 1}`;
        lines.push(`${letter}) ${c.name} = ${fmt(c.amount)}`);
      });
    } else { lines.push("(No expenses recorded)"); }
    lines.push("");
    // Delivery channel totals — dynamic from Delivery Channel Master.
    (activeChannels || []).forEach((ch) => {
      const v = readChannelValue(d, ch, "total_");
      lines.push(`↪️ Total ${ch.label} = ${fmt(v)}`);
    });
    lines.push(
      `↪️ Total Paytm = ${fmt(d.total_paytm)}`,
      `↪️ Total Bharat Pay = ${fmt(d.total_bharat_pay)}`,
      `↪️ Total Cash Sale = ${fmt(d.total_cash_sale)}`,
      `↪️ Total Cash Expenses = ${fmt(d.total_cash_expenses)}`,
      `↪️ Total Cash In Hand = ${fmt(d.total_cash_in_hand)}`,
      `↪️ Total Online Expenses = ${fmt(d.total_online_expenses)}`,
      `↪️ APC = ${Math.round(Number(d.apc) || 0)}`,
      `↪️ Total No. Of Guest = ${Math.round(Number(d.total_guests) || 0)}`,
    );
    setGeneratedText(lines.join("\n"));
  };

  useEffect(() => {
    if (Object.keys(editableData).length > 0) regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData, editableCategories]);

  const copyText = () => {
    navigator.clipboard.writeText(generatedText).then(() => {
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const updateField = (key, value) => setEditableData(p => ({ ...p, [key]: value }));
  const updateCategory = (idx, key, value) => {
    setEditableCategories(prev => prev.map((c, i) => i === idx ? { ...c, [key]: key === "amount" ? (parseFloat(value) || 0) : value } : c));
  };
  const addCategory = () => setEditableCategories(p => [...p, { name: "New", amount: 0 }]);
  const removeCategory = (idx) => setEditableCategories(p => p.filter((_, i) => i !== idx));

  const editFields = [
    { key: "total_sale", label: "Total Sale" },
    { key: "total_card", label: "Total Card" },
    { key: "total_deposit", label: "Total Deposit" },
    { key: "total_withdrawal", label: "Total Withdrawl" },
    // Delivery channel totals — dynamic from Delivery Channel Master.
    ...((activeChannels || []).map((ch) => ({ key: `total_${ch.key}`, label: `Total ${ch.label}` }))),
    { key: "total_paytm", label: "Total Paytm" },
    { key: "total_bharat_pay", label: "Total Bharat Pay" },
    { key: "total_cash_sale", label: "Total Cash Sale" },
    { key: "total_cash_expenses", label: "Total Cash Expenses" },
    { key: "total_online_expenses", label: "Total Online Expenses" },
    { key: "total_cash_in_hand", label: "Total Cash In Hand" },
    { key: "total_guests", label: "Total No. Of Guest" },
    { key: "apc", label: "APC" },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Center</Label>
              <select value={selectedCenter} onChange={e => setSelectedCenter(e.target.value)}
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                data-testid="yearly-center-select" disabled={!isAdmin && !isFranchiseOwner}>
                {!isAdmin && <option value={session?.center}>{session?.center}</option>}
                {isAdmin && centersList.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Year (India: FY start year, Apr → Mar)</Label>
              <Input type="number" min="2015" max="2100" value={year}
                onChange={e => setYear(e.target.value)}
                className="w-[140px]" data-testid="yearly-input" />
              <p className="text-[10px] text-muted-foreground">India FY 2026-27 = enter 2026. International = calendar year.</p>
            </div>
            <Button onClick={generate} disabled={loading} data-testid="yearly-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
              Generate Yearly Text
            </Button>
            <Button onClick={refresh} variant="outline" disabled={loading} data-testid="yearly-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh from Data
            </Button>
            {generatedText && (
              <Button onClick={copyText} variant={copied ? "default" : "outline"}
                className={copied ? "bg-green-600 hover:bg-green-700" : "border-green-500 text-green-600"}
                data-testid="yearly-copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Copy Text"}
              </Button>
            )}
            {textData && (
              <Button variant="outline"
                className="border-rose-500 text-rose-600 hover:bg-rose-50"
                onClick={() => downloadBrandedPdf({
                  session, center: selectedCenter, periodType: "yearly",
                  payload: { year: parseInt(year, 10), include_chart: showChart },
                  filenameHint: `${selectedCenter}_yearly_${year}`,
                  onErr: (m) => toast.error(m), onOk: () => toast.success("PDF downloaded"),
                })}
                data-testid="yearly-pdf-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            )}
            {textData && (
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-2 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100"
                data-testid="yearly-show-chart-toggle">
                <input type="checkbox" checked={showChart}
                  onChange={(e) => setShowChart(e.target.checked)}
                  className="cursor-pointer" />
                <BarChart3 className="w-4 h-4 text-amber-700" />
                <span className="text-amber-800 font-medium">Show Chart</span>
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {textData && showChart && (
        <Card className="border-2 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
              <BarChart3 className="w-4 h-4" /> Sales vs Expenses (Yearly — bucketed by month)
            </CardTitle>
            <CardDescription className="text-xs">
              Total Sales: <strong className="text-[#800020]">Rs. {(textData.data?.total_sale || 0).toLocaleString("en-IN")}</strong>
              {" · "}Total Expenses: <strong className="text-[#C9A227]">Rs. {(textData.data?.total_expenses || 0).toLocaleString("en-IN")}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SalesVsExpensesChart chartSeries={textData.data?.chart_series || []}
              periodLabel={textData.year_label} />
          </CardContent>
        </Card>
      )}

      {textData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {canEdit && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Edit Values</CardTitle>
                <CardDescription className="text-xs">
                  {textData.has_sales_data ? (
                    <Badge className="bg-green-100 text-green-800">Aggregated from {textData.sales_days} day(s) — {textData.year_label}</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">No sales data this year</Badge>
                  )}
                  {textData.expense_count > 0 && (
                    <Badge className="bg-blue-100 text-blue-800 ml-2">{textData.expense_count} expenses</Badge>
                  )}
                  {textData.is_international && (
                    <Badge className="bg-purple-100 text-purple-800 ml-2">International (CY · Doordash shown)</Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {editFields
                    .filter(() => true)
                    .map(f => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className="text-[11px] w-32 text-right text-muted-foreground shrink-0">{f.label}</Label>
                        <Input type="number" value={editableData[f.key] ?? ""} className="h-8 text-xs"
                          onChange={e => updateField(f.key, parseFloat(e.target.value) || 0)}
                          data-testid={`yearly-field-${f.key}`} />
                      </div>
                    ))}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium">Expense Categories (Description Expenses)</Label>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addCategory} data-testid="yearly-add-cat-btn">+ Add</Button>
                  </div>
                  <div className="space-y-1.5">
                    {editableCategories.length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No expense categories</p>
                    )}
                    {editableCategories.map((c, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`yearly-cat-row-${idx}`}>
                        <span className="text-xs text-muted-foreground w-4">{String.fromCharCode(97 + idx)})</span>
                        <Input value={c.name} className="h-8 text-xs flex-1"
                          onChange={e => updateCategory(idx, "name", e.target.value)} />
                        <Input type="number" value={c.amount} className="h-8 text-xs w-28"
                          onChange={e => updateCategory(idx, "amount", e.target.value)} />
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500"
                          onClick={() => removeCategory(idx)} data-testid={`yearly-cat-remove-${idx}`}>×</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-2 border-green-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                <MessageSquare className="w-4 h-4" /> WhatsApp Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm bg-green-50 p-4 rounded-lg font-mono leading-relaxed border"
                data-testid="yearly-text-preview">{generatedText}</pre>
              <Button onClick={copyText} className="mt-3 w-full" variant={copied ? "default" : "outline"}
                data-testid="yearly-copy-btn-bottom">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied to Clipboard!" : "Copy to Clipboard"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// =======================================
// PAGE
// =======================================
export default function DailyTextGenerator() {
  const { session } = useAuth();
  const isAdmin = isAdminUser(session);
  const [centersList, setCentersList] = useState([]);
  const isFranchiseOwner = session?.role_key === "franchise_owner";
  const canEdit = isAdmin || !isFranchiseOwner;

  useEffect(() => {
    if (session?.token) fetchCentersFromDB(session.token).then(setCentersList);
  }, [session?.token]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-primary" data-testid="daily-text-title">Sales Text Generator</h1>
        <p className="text-muted-foreground mt-1">Generate WhatsApp-style daily or weekly sales summaries</p>
      </div>

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="daily" data-testid="tab-daily">
            <FileText className="w-4 h-4 mr-2" /> Daily
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">
            <CalendarRange className="w-4 h-4 mr-2" /> Weekly
          </TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">
            <CalendarDays className="w-4 h-4 mr-2" /> Monthly
          </TabsTrigger>
          <TabsTrigger value="yearly" data-testid="tab-yearly">
            <CalendarClock className="w-4 h-4 mr-2" /> Yearly
          </TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4">
          <DailyTab session={session} centersList={centersList} isAdmin={isAdmin}
            isFranchiseOwner={isFranchiseOwner} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="weekly" className="mt-4">
          <WeeklyTab session={session} centersList={centersList} isAdmin={isAdmin}
            isFranchiseOwner={isFranchiseOwner} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="monthly" className="mt-4">
          <MonthlyTab session={session} centersList={centersList} isAdmin={isAdmin}
            isFranchiseOwner={isFranchiseOwner} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="yearly" className="mt-4">
          <YearlyTab session={session} centersList={centersList} isAdmin={isAdmin}
            isFranchiseOwner={isFranchiseOwner} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
